import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import {
  isMeetingStatus,
  MEETING_DURATION_HOURS,
  toDayKey,
  isBirthdayOn,
  ageOn,
  startOfWeekMonday,
  monthGridDays,
  CZ_DAY_NAMES,
  CZ_MONTH_NAMES,
} from '../utils/calendarUtils';

const START_HOUR = 7;
const END_HOUR = 20;
const HOUR_PX = 46;

function pad(n) { return String(n).padStart(2, '0'); }

// Vytvoří lokální ISO string (bez posunu do UTC) pro odeslání na backend
function localISO(dayKey, timeStr) {
  const [y, m, d] = dayKey.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm).toISOString();
}

export default function Calendar({ currentUserId, userRole, users, isDarkMode }) {
  const navigate = useNavigate();
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [viewUserId, setViewUserId] = useState('');
  const [data, setData] = useState({ activities: [], events: [], dayOffs: [], birthdays: [], visibleUsers: [], allUsers: [], isAdmin: false });
  const [loading, setLoading] = useState(true);
  const [dayModal, setDayModal] = useState(null); // dayKey
  const [eventModal, setEventModal] = useState(null); // { mode:'create'|'edit', event, dayKey }
  const [feedModal, setFeedModal] = useState(false); // modal s odkazem na odběr kalendáře

  // --- Rozsah pro fetch ---
  const range = useMemo(() => {
    if (view === 'month') {
      const days = monthGridDays(cursor);
      const from = new Date(days[0]); from.setHours(0, 0, 0, 0);
      const to = new Date(days[41]); to.setHours(23, 59, 59, 999);
      return { from, to, days };
    }
    const start = startOfWeekMonday(cursor);
    const days = [];
    for (let i = 0; i < 7; i++) { const d = new Date(start); d.setDate(start.getDate() + i); days.push(d); }
    const from = new Date(days[0]); from.setHours(0, 0, 0, 0);
    const to = new Date(days[6]); to.setHours(23, 59, 59, 999);
    return { from, to, days };
  }, [view, cursor]);

  const fetchData = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        userId: currentUserId,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
      });
      if (viewUserId) params.set('viewUserId', viewUserId);
      const res = await fetch(`${API_URL}/calendar?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({
        activities: json.activities || [],
        events: json.events || [],
        dayOffs: json.dayOffs || [],
        birthdays: json.birthdays || [],
        visibleUsers: json.visibleUsers || [],
        allUsers: json.allUsers || [],
        isAdmin: !!json.isAdmin,
      });
    } catch (err) {
      console.error('Chyba při načítání kalendáře:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUserId, range.from, range.to, viewUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- Indexace dat po dnech ---
  const byDay = useMemo(() => {
    const activities = {}; // dayKey -> [] (bez schůzek)
    const meetings = {};   // dayKey -> [] (Schůzka domluvena)
    const dayOffs = {};    // dayKey -> { personal:[] }
    const birthdays = {};  // dayKey -> [{name, age}]

    const todayKey = toDayKey(new Date());

    (data.activities || []).forEach(a => {
      if (!a.nextActionDate) return;
      const key = toDayKey(a.nextActionDate);
      // Aktivity z minulých dnů nezobrazujeme — jen dnešek a budoucnost
      if (key < todayKey) return;
      if (isMeetingStatus(a.status)) {
        (meetings[key] = meetings[key] || []).push(a);
      } else {
        (activities[key] = activities[key] || []).push(a);
      }
    });

    (data.dayOffs || []).forEach(o => {
      const key = o.date;
      if (!dayOffs[key]) dayOffs[key] = { personal: [] };
      if (o.userId) dayOffs[key].personal.push(o);
    });

    range.days.forEach(d => {
      const key = toDayKey(d);
      (data.birthdays || []).forEach(b => {
        if (isBirthdayOn(b.birthDate, d)) {
          (birthdays[key] = birthdays[key] || []).push({ name: b.name, age: ageOn(b.birthDate, d), userId: b.userId });
        }
      });
    });

    return { activities, meetings, dayOffs, birthdays };
  }, [data, range.days]);

  const eventsForDay = useCallback((d) => {
    const dayStart = new Date(d); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(d); dayEnd.setHours(23, 59, 59, 999);
    return (data.events || []).filter(ev => new Date(ev.start) <= dayEnd && new Date(ev.end) >= dayStart);
  }, [data.events]);

  // --- Navigace ---
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setCursor(d); };
  const step = (dir) => {
    const d = new Date(cursor);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + dir * 7);
    setCursor(d);
  };

  const canSwitchUsers = data.isAdmin || (data.visibleUsers && data.visibleUsers.length > 1);
  const participantOptions = (data.allUsers && data.allUsers.length ? data.allUsers : (users || []))
    .filter(u => String(u._id) !== String(currentUserId));

  // --- Volno toggle (osobní) ---
  const toggleDayOff = async (dayKey) => {
    const existing = byDay.dayOffs[dayKey];
    const has = (existing?.personal || []).some(p => String(p.userId) === String(currentUserId));
    try {
      if (has) {
        await fetch(`${API_URL}/calendar/dayoffs`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId, date: dayKey }),
        });
      } else {
        await fetch(`${API_URL}/calendar/dayoffs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUserId, date: dayKey }),
        });
      }
      await fetchData();
    } catch (err) {
      console.error('Chyba při změně volna:', err);
      alert('Nepodařilo se změnit volno.');
    }
  };

  const deleteEvent = async (ev) => {
    let deleteSeries = false;
    if (ev.seriesId) {
      const all = window.confirm(`Tato 1:1 se opakuje. Kliknutím na OK smažete VŠECHNY budoucí výskyty, "Zrušit" smaže jen tento.`);
      deleteSeries = all;
      if (!all && !window.confirm(`Smazat pouze tento výskyt události "${ev.title}"?`)) return;
    } else if (!window.confirm(`Smazat událost "${ev.title}"?`)) {
      return;
    }
    try {
      await fetch(`${API_URL}/calendar/events/${ev._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId, deleteSeries }),
      });
      await fetchData();
    } catch (err) {
      console.error('Chyba při mazání události:', err);
    }
  };

  const todayKey = toDayKey(new Date());
  const bg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-blue-100 dark:from-gray-800 dark:to-gray-900 py-6">
      <div className="w-[98vw] max-w-[1800px] mx-auto px-4">
        {/* Hlavička */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-800 dark:hover:bg-blue-700 dark:text-white rounded-lg shadow-md transition font-semibold flex-shrink-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Zpět na seznam firem
          </button>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">📅 Kalendář</h1>
        </div>

        {/* Ovládací panel */}
        <div className={`${bg} rounded-lg shadow border ${border} p-3 mb-4 flex flex-wrap items-center gap-2`}>
          <div className="flex items-center gap-1">
            <button onClick={() => step(-1)} className={`px-3 py-1.5 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>‹</button>
            <button onClick={goToday} className={`px-3 py-1.5 rounded font-medium ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>Dnes</button>
            <button onClick={() => step(1)} className={`px-3 py-1.5 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>›</button>
          </div>
          <div className="text-lg font-semibold text-gray-800 dark:text-white min-w-[180px]">
            {view === 'month'
              ? `${CZ_MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`
              : `Týden ${range.days[0].getDate()}.${range.days[0].getMonth() + 1}. – ${range.days[6].getDate()}.${range.days[6].getMonth() + 1}.${range.days[6].getFullYear()}`}
          </div>

          <div className="flex items-center rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 ml-auto">
            <button onClick={() => setView('month')} className={`px-4 py-1.5 text-sm font-medium ${view === 'month' ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Měsíc</button>
            <button onClick={() => setView('week')} className={`px-4 py-1.5 text-sm font-medium ${view === 'week' ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Týden</button>
          </div>

          {canSwitchUsers && (
            <select
              value={viewUserId}
              onChange={(e) => setViewUserId(e.target.value)}
              className={`px-3 py-1.5 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="">{data.isAdmin ? 'Všichni uživatelé' : 'Můj tým'}</option>
              {(data.visibleUsers || []).map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          )}

          <button
            onClick={() => setEventModal({ mode: 'create', dayKey: todayKey })}
            className="px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow"
          >
            + Naplánovat 1:1 / událost
          </button>
          <button
            onClick={() => setFeedModal(true)}
            title="Připojit do Google / Apple kalendáře"
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold shadow ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
          >
            🔗 Připojit kalendář
          </button>
        </div>

        {/* Legenda */}
        <div className="flex flex-wrap gap-3 mb-3 text-xs">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block"></span> Aktivity</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500 inline-block"></span> Schůzka (rezervace 2 h)</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block"></span> 1:1 / událost</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-500 inline-block"></span> Firemní událost</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500 inline-block"></span> Moje volno</span>
          <span className="inline-flex items-center gap-1">🎂 Narozeniny</span>
        </div>

        {loading && (
          <div className="mb-2 text-sm text-gray-500 dark:text-gray-400">Načítám…</div>
        )}

        {view === 'month'
          ? <MonthView {...{ range, byDay, eventsForDay, isDarkMode, todayKey, cursor, setDayModal, currentUserId }} />
          : <WeekView {...{ range, byDay, eventsForDay, isDarkMode, todayKey, setDayModal, setEventModal, currentUserId }} />}
      </div>

      {dayModal && (
        <DayModal
          dayKey={dayModal}
          byDay={byDay}
          events={eventsForDay(new Date(dayModal + 'T12:00:00'))}
          isDarkMode={isDarkMode}
          isAdmin={data.isAdmin}
          currentUserId={currentUserId}
          onClose={() => setDayModal(null)}
          onToggleDayOff={toggleDayOff}
          onNewEvent={(dayKey) => { setDayModal(null); setEventModal({ mode: 'create', dayKey }); }}
          onEditEvent={(ev) => { setDayModal(null); setEventModal({ mode: 'edit', event: ev, dayKey: toDayKey(ev.start) }); }}
          onDeleteEvent={deleteEvent}
          navigate={navigate}
        />
      )}

      {eventModal && (
        <EventModal
          initial={eventModal}
          isDarkMode={isDarkMode}
          currentUserId={currentUserId}
          participantOptions={participantOptions}
          isAdmin={data.isAdmin}
          onClose={() => setEventModal(null)}
          onSaved={async () => { setEventModal(null); await fetchData(); }}
        />
      )}

      {feedModal && (
        <FeedModal
          isDarkMode={isDarkMode}
          currentUserId={currentUserId}
          onClose={() => setFeedModal(false)}
        />
      )}
    </div>
  );
}

// ============ MĚSÍČNÍ POHLED ============
function MonthView({ range, byDay, eventsForDay, isDarkMode, todayKey, cursor, setDayModal, currentUserId }) {
  const cellBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  return (
    <div className={`rounded-lg overflow-hidden border ${border} shadow`}>
      <div className="grid grid-cols-7">
        {CZ_DAY_NAMES.map((n, i) => (
          <div key={n} className={`py-2 text-center text-xs font-semibold uppercase tracking-wide ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'} ${i >= 5 ? 'opacity-80' : ''}`}>{n}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {range.days.map((d) => {
          const key = toDayKey(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const acts = byDay.activities[key] || [];
          const meets = byDay.meetings[key] || [];
          const evs = eventsForDay(d);
          const off = byDay.dayOffs[key];
          const bdays = byDay.birthdays[key] || [];
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const myOff = (off?.personal || []).find(p => String(p.userId) === String(currentUserId));
          const otherOffs = (off?.personal || []).filter(p => String(p.userId) !== String(currentUserId));

          return (
            <div
              key={key}
              onClick={() => setDayModal(key)}
              className={`min-h-[118px] p-1.5 border-b border-r ${border} cursor-pointer transition
                ${myOff ? (isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50') : cellBg}
                ${!inMonth ? 'opacity-45' : ''}
                ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-blue-50'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-full
                  ${isToday ? 'bg-blue-600 text-white' : (isWeekend ? (isDarkMode ? 'text-gray-500' : 'text-gray-400') : (isDarkMode ? 'text-gray-200' : 'text-gray-700'))}`}>
                  {d.getDate()}
                </span>
                <div className="flex items-center gap-1">
                  {myOff && <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase">Mé volno</span>}
                  {acts.length > 0 && (
                    <span
                      className="text-[11px] font-bold rounded px-1.5 py-0.5 bg-blue-500 text-white"
                      title={`${acts.length} ${acts.length === 1 ? 'aktivita' : acts.length < 5 ? 'aktivity' : 'aktivit'}`}
                    >
                      📋 {acts.length}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                {myOff && (
                  <div className="text-[11px] truncate rounded px-1 bg-amber-500 text-white" title="Máte označené volno">🌴 Moje volno</div>
                )}
                {otherOffs.map((p, i) => (
                  <div key={i} className="text-[11px] truncate text-amber-600 dark:text-amber-300" title={`${p.userName} má volno`}>🌴 {p.userName}</div>
                ))}
                {bdays.map((b, i) => (
                  <div key={i} className="text-[11px] truncate text-pink-600 dark:text-pink-300" title={`${b.name} má narozeniny`}>🎂 {b.name}</div>
                ))}
                {meets.map((m, i) => (
                  <div key={i} className="text-[11px] truncate rounded px-1 bg-purple-500 text-white" title={`Schůzka: ${m.name}`}>
                    🤝 {new Date(m.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} {m.name}
                  </div>
                ))}
                {evs.slice(0, 2).map((ev, i) => (
                  <div key={i} className={`text-[11px] truncate rounded px-1 text-white ${ev.type === 'announcement' || ev.audienceAll ? 'bg-indigo-600' : 'bg-green-600'}`} title={ev.title}>
                    {ev.type === 'announcement' || ev.audienceAll ? '📢' : (ev.type === 'oneToOne' ? '👥' : '📌')} {ev.title}
                  </div>
                ))}
                {evs.length > 2 && <div className="text-[10px] text-green-700 dark:text-green-300">+{evs.length - 2} událostí</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ TÝDENNÍ POHLED (po hodinách) ============
function WeekView({ range, byDay, eventsForDay, isDarkMode, todayKey, setDayModal, setEventModal, currentUserId }) {
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const bg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const hours = [];
  for (let h = START_HOUR; h <= END_HOUR; h++) hours.push(h);
  const gridHeight = (END_HOUR - START_HOUR + 1) * HOUR_PX;

  const timedItems = (d) => {
    const items = [];
    const key = toDayKey(d);
    (byDay.meetings[key] || []).forEach(m => {
      const start = new Date(m.nextActionDate);
      items.push({ kind: 'meeting', title: m.name, start, durationH: MEETING_DURATION_HOURS, sub: `🤝 Schůzka` });
    });
    eventsForDay(d).forEach(ev => {
      const start = new Date(ev.start);
      const end = new Date(ev.end);
      const durationH = Math.max(0.5, (end - start) / 3600000);
      const isAnn = ev.type === 'announcement' || ev.audienceAll;
      items.push({ kind: 'event', title: ev.title, start, durationH, sub: isAnn ? '📢 Pro tým' : (ev.type === 'oneToOne' ? '👥 1:1' : '📌'), raw: ev, announcement: isAnn });
    });
    return items;
  };

  const posStyle = (start, durationH) => {
    const minutes = start.getHours() * 60 + start.getMinutes() - START_HOUR * 60;
    const top = Math.max(0, (minutes / 60) * HOUR_PX);
    const height = Math.max(22, durationH * HOUR_PX - 2);
    return { top, height };
  };

  return (
    <div className={`rounded-lg overflow-hidden border ${border} shadow ${bg}`}>
      {/* Hlavička dnů */}
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(7, 1fr)` }}>
        <div className={`border-b border-r ${border}`}></div>
        {range.days.map(d => {
          const key = toDayKey(d);
          const isToday = key === todayKey;
          const off = byDay.dayOffs[key];
          const bdays = byDay.birthdays[key] || [];
          const acts = byDay.activities[key] || [];
          const myOff = (off?.personal || []).find(p => String(p.userId) === String(currentUserId));
          return (
            <div key={key} className={`border-b border-r ${border} p-1 text-center ${myOff ? (isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50') : ''}`}>
              <div className={`text-xs font-semibold ${isToday ? 'text-blue-600 dark:text-blue-400' : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`}>
                {CZ_DAY_NAMES[(d.getDay() + 6) % 7]} {d.getDate()}.{d.getMonth() + 1}.
              </div>
              {myOff && <div className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase">🌴 Mé volno</div>}
              {bdays.map((b, i) => <div key={i} className="text-[10px] truncate text-pink-600 dark:text-pink-300">🎂 {b.name}</div>)}
              {acts.length > 0 && (
                <button onClick={() => setDayModal(key)} className="mt-0.5 text-[10px] rounded px-1 bg-blue-500 text-white">📋 {acts.length} aktivit</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Mřížka hodin */}
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(7, 1fr)` }}>
        {/* Sloupec s hodinami */}
        <div className="relative" style={{ height: gridHeight }}>
          {hours.map((h, i) => (
            <div key={h} className={`absolute left-0 right-0 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} pr-1 text-right`} style={{ top: i * HOUR_PX - 6 }}>
              {pad(h)}:00
            </div>
          ))}
        </div>
        {/* Dny */}
        {range.days.map(d => {
          const key = toDayKey(d);
          const items = timedItems(d);
          return (
            <div key={key} className={`relative border-r ${border}`} style={{ height: gridHeight }}>
              {hours.map((h, i) => (
                <div
                  key={h}
                  onClick={() => setEventModal({ mode: 'create', dayKey: key, startHour: h })}
                  className={`absolute left-0 right-0 border-b ${border} cursor-pointer ${isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-blue-50'}`}
                  style={{ top: i * HOUR_PX, height: HOUR_PX }}
                ></div>
              ))}
              {items.map((it, idx) => {
                const { top, height } = posStyle(it.start, it.durationH);
                const color = it.kind === 'meeting' ? 'bg-purple-500' : (it.announcement ? 'bg-indigo-600' : 'bg-green-600');
                return (
                  <div
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); if (it.raw) setEventModal({ mode: 'edit', event: it.raw, dayKey: key }); }}
                    className={`absolute left-0.5 right-0.5 rounded px-1 py-0.5 text-white text-[10px] overflow-hidden shadow ${color}`}
                    style={{ top, height }}
                    title={`${it.title} — ${it.start.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`}
                  >
                    <div className="font-semibold truncate">{it.start.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} {it.title}</div>
                    <div className="opacity-90 truncate">{it.sub}{it.kind === 'meeting' ? ` (2 h)` : ''}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ MODAL DNE ============
function DayModal({ dayKey, byDay, events, isDarkMode, isAdmin, currentUserId, onClose, onToggleDayOff, onNewEvent, onEditEvent, onDeleteEvent, navigate }) {
  const acts = byDay.activities[dayKey] || [];
  const meets = byDay.meetings[dayKey] || [];
  const off = byDay.dayOffs[dayKey];
  const bdays = byDay.birthdays[dayKey] || [];
  const [y, m, d] = dayKey.split('-').map(Number);
  const dateLabel = new Date(y, m - 1, d).toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const hasPersonalOff = (off?.personal || []).some(p => String(p.userId) === String(currentUserId));

  const panel = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const sub = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`w-full max-w-lg rounded-xl shadow-2xl ${panel} max-h-[85vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <h3 className="text-lg font-bold capitalize">{dateLabel}</h3>
          <button onClick={onClose} className={sub}>✕</button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {bdays.length > 0 && (
            <div className="rounded bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-200 px-3 py-2 text-sm">
              🎂 Narozeniny: {bdays.map(b => b.name + (b.age ? ` (${b.age})` : '')).join(', ')}
            </div>
          )}

          {meets.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase mb-1 text-purple-600 dark:text-purple-300">Schůzky (rezervace 2 h)</div>
              {meets.map((mt, i) => (
                <div key={i} className="flex items-center justify-between gap-2 rounded px-2 py-1 bg-purple-50 dark:bg-purple-900/20 mb-1">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{new Date(mt.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} · {mt.name}</div>
                    <div className={`text-xs ${sub} truncate`}>IČO {mt.ico || '—'} · {mt.ownerName}</div>
                  </div>
                  <button onClick={() => navigate(`/companies/${mt.companyId}`)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded flex-shrink-0">Detail</button>
                </div>
              ))}
            </div>
          )}

          {events.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase mb-1 text-green-600 dark:text-green-300">Události / 1:1</div>
              {events.map((ev, i) => {
                const isAnn = ev.type === 'announcement' || ev.audienceAll;
                return (
                <div key={i} className={`flex items-center justify-between gap-2 rounded px-2 py-1 mb-1 ${isAnn ? 'bg-indigo-50 dark:bg-indigo-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{isAnn ? '📢' : (ev.type === 'oneToOne' ? '👥' : '📌')} {ev.title}{ev.seriesId ? ' 🔁' : ''}</div>
                    <div className={`text-xs ${sub} truncate`}>
                      {new Date(ev.start).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}–{new Date(ev.end).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                      {isAnn ? (ev.audienceAll ? ' · celý tým' : (ev.participantNames?.length ? ` · ${ev.participantNames.join(', ')}` : '')) : (ev.participantNames?.length ? ` · s ${ev.participantNames.join(', ')}` : '')}
                      {ev.ownerName ? ` · ${ev.ownerName}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {(isAdmin || String(ev.ownerId) === String(currentUserId)) && (
                      <>
                        <button onClick={() => onEditEvent(ev)} className="text-xs px-2 py-1 bg-gray-500 text-white rounded">Upravit</button>
                        <button onClick={() => onDeleteEvent(ev)} className="text-xs px-2 py-1 bg-red-600 text-white rounded">Smazat</button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {acts.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase mb-1 text-blue-600 dark:text-blue-300">Aktivity ({acts.length})</div>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {acts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 rounded px-2 py-1 bg-blue-50 dark:bg-blue-900/20">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{new Date(a.nextActionDate).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })} · {a.name}</div>
                      <div className={`text-xs ${sub} truncate`}>{a.status || '—'} · {a.ownerName}</div>
                    </div>
                    <button onClick={() => navigate(`/companies/${a.companyId}`)} className="text-xs px-2 py-1 bg-blue-600 text-white rounded flex-shrink-0">Detail</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {acts.length === 0 && meets.length === 0 && events.length === 0 && bdays.length === 0 && (
            <div className={`text-sm ${sub} text-center py-4`}>Žádné naplánované položky.</div>
          )}
        </div>

        <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-wrap gap-2 justify-between`}>
          <div className="flex gap-2">
            <button onClick={() => onToggleDayOff(dayKey)} className={`text-sm px-3 py-1.5 rounded ${hasPersonalOff ? 'bg-red-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>
              {hasPersonalOff ? 'Zrušit moje volno' : 'Moje volno'}
            </button>
          </div>
          <button onClick={() => onNewEvent(dayKey)} className="text-sm px-3 py-1.5 rounded bg-green-600 text-white font-semibold">+ Naplánovat 1:1 / událost</button>
        </div>
      </div>
    </div>
  );
}

// ============ MODAL UDÁLOSTI ============
function EventModal({ initial, isDarkMode, currentUserId, participantOptions, isAdmin, onClose, onSaved }) {
  const isEdit = initial.mode === 'edit';
  const ev = initial.event;
  const startH = initial.startHour != null ? pad(initial.startHour) + ':00' : '09:00';
  const [form, setForm] = useState(() => {
    if (isEdit && ev) {
      const s = new Date(ev.start); const e = new Date(ev.end);
      const isAnnouncement = ev.type === 'announcement' || ev.audienceAll;
      return {
        title: ev.title || '',
        type: isAnnouncement ? 'announcement' : (ev.type || 'personal'),
        audienceMode: ev.audienceAll ? 'all' : 'selected',
        dayKey: toDayKey(ev.start),
        startTime: `${pad(s.getHours())}:${pad(s.getMinutes())}`,
        endTime: `${pad(e.getHours())}:${pad(e.getMinutes())}`,
        participantId: ev.participantIds?.[0] ? String(ev.participantIds[0]) : '',
        participantIds: (ev.participantIds || []).map(String),
        location: ev.location || '',
        description: ev.description || '',
        recurrence: 'none',
        occurrences: 8,
      };
    }
    const endDefault = initial.startHour != null ? pad(initial.startHour + 1) + ':00' : '10:00';
    return {
      title: '', type: 'oneToOne', audienceMode: 'all', dayKey: initial.dayKey, startTime: startH, endTime: endDefault,
      participantId: '', participantIds: [], location: '', description: '',
      recurrence: 'none', occurrences: 8,
    };
  });
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleParticipant = (id) => setForm(p => {
    const has = p.participantIds.includes(id);
    return { ...p, participantIds: has ? p.participantIds.filter(x => x !== id) : [...p.participantIds, id] };
  });

  const save = async () => {
    if (!form.title.trim()) { alert('Zadejte název události.'); return; }
    if (form.type === 'oneToOne' && !form.participantId) { alert('Vyberte osobu pro 1:1.'); return; }
    if (form.type === 'announcement' && form.audienceMode === 'selected' && form.participantIds.length === 0) {
      alert('Vyberte alespoň jednoho uživatele.'); return;
    }
    if (form.endTime <= form.startTime) { alert('Konec musí být po začátku.'); return; }
    setSaving(true);
    try {
      let participantIds = [];
      let audienceAll = false;
      if (form.type === 'oneToOne') {
        participantIds = form.participantId ? [form.participantId] : [];
      } else if (form.type === 'announcement') {
        if (form.audienceMode === 'all') audienceAll = true;
        else participantIds = form.participantIds;
      }
      const payload = {
        userId: currentUserId,
        title: form.title.trim(),
        type: form.type,
        description: form.description,
        location: form.location,
        start: localISO(form.dayKey, form.startTime),
        end: localISO(form.dayKey, form.endTime),
        participantIds,
        audienceAll,
      };
      // Opakování lze nastavit jen u nové 1:1 schůzky
      if (!isEdit && form.type === 'oneToOne' && form.recurrence === 'weekly') {
        payload.recurrence = 'weekly';
        payload.occurrences = form.occurrences;
      }
      const url = isEdit ? `${API_URL}/calendar/events/${ev._id}` : `${API_URL}/calendar/events`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await onSaved();
    } catch (err) {
      console.error('Chyba při ukládání události:', err);
      alert('Nepodařilo se uložit událost.');
    } finally {
      setSaving(false);
    }
  };

  const panel = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const inputCls = `w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`;
  const label = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`w-full max-w-md rounded-xl shadow-2xl ${panel} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <h3 className="text-lg font-bold">{isEdit ? 'Upravit událost' : 'Nová událost'}</h3>
          <button onClick={onClose} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>✕</button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div>
            <label className={label}>Typ</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setF('type', 'oneToOne')} className={`flex-1 min-w-[90px] px-3 py-1.5 rounded text-sm font-medium ${form.type === 'oneToOne' ? 'bg-green-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>👥 1:1 schůzka</button>
              <button onClick={() => setF('type', 'personal')} className={`flex-1 min-w-[90px] px-3 py-1.5 rounded text-sm font-medium ${form.type === 'personal' ? 'bg-green-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>📌 Osobní</button>
              {isAdmin && (
                <button onClick={() => setF('type', 'announcement')} className={`flex-1 min-w-[90px] px-3 py-1.5 rounded text-sm font-medium ${form.type === 'announcement' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>📢 Pro tým</button>
              )}
            </div>
          </div>
          <div>
            <label className={label}>Název</label>
            <input className={inputCls} value={form.title} onChange={e => setF('title', e.target.value)} placeholder={form.type === 'announcement' ? 'Např. Firemní porada' : 'Např. 1:1 s Petrem'} />
          </div>
          {form.type === 'oneToOne' && (
            <div>
              <label className={label}>S kým</label>
              <select className={inputCls} value={form.participantId} onChange={e => setF('participantId', e.target.value)}>
                <option value="">— vyberte —</option>
                {participantOptions.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </div>
          )}
          {form.type === 'oneToOne' && !isEdit && (
            <div>
              <label className={label}>Opakování</label>
              <div className="flex gap-2">
                <button onClick={() => setF('recurrence', 'none')} className={`flex-1 px-3 py-1.5 rounded text-sm font-medium ${form.recurrence === 'none' ? 'bg-green-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>Jednorázově</button>
                <button onClick={() => setF('recurrence', 'weekly')} className={`flex-1 px-3 py-1.5 rounded text-sm font-medium ${form.recurrence === 'weekly' ? 'bg-green-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>🔁 Každý týden</button>
              </div>
              {form.recurrence === 'weekly' && (
                <div className="mt-2 flex items-center gap-2">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Počet týdnů:</span>
                  <input
                    type="number" min={2} max={52}
                    className={`w-20 px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={form.occurrences}
                    onChange={e => setF('occurrences', Math.min(52, Math.max(2, parseInt(e.target.value, 10) || 2)))}
                  />
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(vč. prvního)</span>
                </div>
              )}
            </div>
          )}
          {form.type === 'announcement' && (
            <div>
              <label className={label}>Komu</label>
              <div className="flex gap-2 mb-2">
                <button onClick={() => setF('audienceMode', 'all')} className={`flex-1 px-3 py-1.5 rounded text-sm font-medium ${form.audienceMode === 'all' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>🌐 Všem</button>
                <button onClick={() => setF('audienceMode', 'selected')} className={`flex-1 px-3 py-1.5 rounded text-sm font-medium ${form.audienceMode === 'selected' ? 'bg-indigo-600 text-white' : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100')}`}>✅ Vybraným</button>
              </div>
              {form.audienceMode === 'selected' && (
                <div className={`max-h-44 overflow-y-auto rounded border p-2 space-y-1 ${isDarkMode ? 'border-gray-600 bg-gray-700/40' : 'border-gray-300 bg-gray-50'}`}>
                  {participantOptions.length === 0 && <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádní uživatelé.</div>}
                  {participantOptions.map(u => (
                    <label key={u._id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={form.participantIds.includes(String(u._id))} onChange={() => toggleParticipant(String(u._id))} />
                      <span className="truncate">{u.name}</span>
                    </label>
                  ))}
                  {form.audienceMode === 'selected' && form.participantIds.length > 0 && (
                    <div className="text-xs text-indigo-600 dark:text-indigo-300 pt-1">Vybráno: {form.participantIds.length}</div>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label className={label}>Datum</label>
            <input type="date" className={inputCls} value={form.dayKey} onChange={e => setF('dayKey', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label}>Od</label>
              <input type="time" className={inputCls} value={form.startTime} onChange={e => setF('startTime', e.target.value)} />
            </div>
            <div>
              <label className={label}>Do</label>
              <input type="time" className={inputCls} value={form.endTime} onChange={e => setF('endTime', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label}>Místo (volitelné)</label>
            <input className={inputCls} value={form.location} onChange={e => setF('location', e.target.value)} />
          </div>
          <div>
            <label className={label}>Poznámka (volitelné)</label>
            <textarea className={inputCls} rows={2} value={form.description} onChange={e => setF('description', e.target.value)} />
          </div>
        </div>
        <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-2`}>
          <button onClick={onClose} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200'}`}>Zrušit</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded text-sm bg-green-600 text-white font-semibold disabled:opacity-60">{saving ? 'Ukládám…' : 'Uložit'}</button>
        </div>
      </div>
    </div>
  );
}

// ============ MODAL ODBĚRU KALENDÁŘE (Google / Apple) ============
function FeedModal({ isDarkMode, currentUserId, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/calendar/feed-url?userId=${currentUserId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setInfo(json);
    } catch (err) {
      console.error('Chyba při načítání odkazu:', err);
      setError('Nepodařilo se načíst odkaz.');
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!info?.url) return;
    try {
      await navigator.clipboard.writeText(info.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback – vyber text ručně
    }
  };

  const reset = async () => {
    if (!window.confirm('Opravdu vygenerovat nový odkaz? Starý odkaz přestane fungovat a odběr bude nutné znovu přidat.')) return;
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/calendar/feed-url/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setInfo(json);
    } catch (err) {
      console.error('Chyba při obnově odkazu:', err);
      alert('Nepodařilo se vygenerovat nový odkaz.');
    } finally {
      setResetting(false);
    }
  };

  const panel = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const label = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;
  const boxCls = `w-full px-3 py-2 rounded border text-xs font-mono break-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-gray-50 border-gray-300 text-gray-800'}`;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`w-full max-w-lg rounded-xl shadow-2xl ${panel} max-h-[90vh] flex flex-col`} onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <h3 className="text-lg font-bold">🔗 Připojit kalendář</h3>
          <button onClick={onClose} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>✕</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto text-sm">
          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
            Odebírejte svůj CRM kalendář (schůzky, aktivity, události a volno) v Google nebo Apple kalendáři.
            Odběr se aktualizuje automaticky. Odkaz je osobní – <strong>nesdílejte ho</strong>.
          </p>

          {loading && <div className="text-gray-500">Načítám odkaz…</div>}
          {error && (
            <div className="text-red-500">
              {error} <button onClick={load} className="underline">Zkusit znovu</button>
            </div>
          )}

          {info && !loading && (
            <>
              {/* Hlavní tlačítko pro telefon — webcal otevře nativní aplikaci Kalendář */}
              <a
                href={info.webcalUrl}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow"
              >
                📲 Přidat do kalendáře v telefonu
              </a>
              <p className={`text-[11px] text-center -mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Na iPhonu/iPadu otevře přímo aplikaci Kalendář a nabídne přidání odběru.
              </p>

              <div>
                <label className={label}>Odkaz na odběr</label>
                <div className={boxCls}>{info.url}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={copy} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
                    {copied ? '✓ Zkopírováno' : 'Kopírovat odkaz'}
                  </button>
                  <a
                    href={info.webcalUrl}
                    className="px-3 py-1.5 rounded bg-gray-500 hover:bg-gray-600 text-white text-xs font-semibold"
                  >
                    Otevřít v Apple kalendáři
                  </a>
                </div>
              </div>

              <div className={`rounded-lg p-3 text-xs ${isDarkMode ? 'bg-gray-700/50' : 'bg-blue-50'}`}>
                <p className="font-semibold mb-1">🍏 Apple kalendář (Mac / iPhone)</p>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                  Na telefonu klepněte na <em>„Přidat do kalendáře v telefonu“</em>.
                  Na Macu zvolte v aplikaci Kalendář <em>Soubor → Nový odběr kalendáře</em> a vložte zkopírovaný odkaz.
                </p>
              </div>

              <div className={`rounded-lg p-3 text-xs ${isDarkMode ? 'bg-gray-700/50' : 'bg-green-50'}`}>
                <p className="font-semibold mb-1">📆 Google kalendář</p>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                  Na počítači otevřete <em>Google Kalendář → Jiné kalendáře → + → Z adresy URL</em>,
                  vložte zkopírovaný odkaz a potvrďte <em>Přidat kalendář</em>. Odběr se pak zobrazí
                  i v aplikaci Google Kalendář v telefonu. Google aktualizuje odběr obvykle jednou za několik hodin.
                </p>
              </div>

              <div className="pt-2 border-t border-dashed border-gray-400/30">
                <button onClick={reset} disabled={resetting} className="text-xs text-red-500 hover:underline disabled:opacity-60">
                  {resetting ? 'Generuji…' : 'Vygenerovat nový odkaz (zneplatní stávající)'}
                </button>
              </div>
            </>
          )}
        </div>
        <div className={`px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end`}>
          <button onClick={onClose} className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200'}`}>Zavřít</button>
        </div>
      </div>
    </div>
  );
}
